import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { useVoiceCall, CallStatus } from "@aihumanity/voice-sdk/react";

import TopMenuBar from "components/TopMenuBar";
import LeadCaptureModal from "components/LeadCaptureModal";
import AnimatedVoiceFigure from "components/AnimatedVoiceFigure";
import { DEFAULT_VOICE_CHARACTER_ID } from "components/AvatarCharacter/characters";
import {
  affectStateFromApiFusion,
  affectContextMessage,
  confirmAdaptation,
  fuseAffect,
  observationFromScores,
  sentContextFingerprint,
  shouldSendAffectContext,
} from "./affectFusion";
import useFaceEmotionSampler from "./useFaceEmotionSampler";
import {
  DETECTION_MODES,
  ScoreList,
  SessionReportCard,
  SignalCard,
  Timeline,
} from "./panels";
import { buildSessionReport } from "./sessionReport";
import { ensureDemoSession } from "./demoSession";
import "./style.css";
import "./fusion.css";

const VOICE_API_URL = process.env.REACT_APP_VOICE_API_URL || "";
const VOICE_AVATAR_ID = process.env.REACT_APP_VOICE_AVATAR_ID
  || DEFAULT_VOICE_CHARACTER_ID;
let demoAudioContext = null;

function toWsBase(httpUrl) {
  return httpUrl
    .replace(/^https:\/\//i, "wss://")
    .replace(/^http:\/\//i, "ws://");
}

function ensureAudioContext() {
  if (typeof window === "undefined") return null;
  if (!demoAudioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    demoAudioContext = new AudioContextClass();
  }
  if (demoAudioContext.state === "suspended") {
    void demoAudioContext.resume().catch(() => {});
  }
  return demoAudioContext;
}

async function fetchJoinUrl() {
  await ensureDemoSession();
  const response = await fetch("/.netlify/functions/create-demo-call", {
    method: "POST",
    credentials: "same-origin",
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const required = Array.isArray(payload.required)
      ? ` Missing: ${payload.required.join(", ")}.`
      : "";
    throw new Error(
      `${payload.error || "Could not create demo call."}${required}`,
    );
  }
  return payload;
}

const VOICE_OPTIONS = {
  fetchJoinUrl,
  get audioContext() {
    return ensureAudioContext();
  },
};

const STATUS_COPY = {
  [CallStatus.IDLE]: "Ready for a conversation",
  [CallStatus.CONNECTING]: "Opening a realtime voice channel",
  [CallStatus.CONNECTED]: "Connected",
  [CallStatus.LISTENING]: "Listening to you",
  [CallStatus.THINKING]: "Adapting the response",
  [CallStatus.SPEAKING]: "Speaking",
  [CallStatus.DISCONNECTING]: "Closing the call",
  [CallStatus.DISCONNECTED]: "Call complete",
};

export default function VoiceAgentDemo() {
  const location = useLocation();
  const personaPrompt = location.state?.personaPrompt || null;
  const businessName = location.state?.businessName || null;
  const [audioContext, setAudioContext] = useState(null);
  const [scores, setScores] = useState(null);
  const [liveEmotion, setLiveEmotion] = useState(null);
  const [mode, setMode] = useState("fusion");
  const [timeline, setTimeline] = useState([]);
  const [lastContext, setLastContext] = useState("");
  const [learnedAffectState, setLearnedAffectState] = useState(null);
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [affectHistory, setAffectHistory] = useState([]);
  const [sessionReport, setSessionReport] = useState(null);
  const callStartedAtRef = useRef(null);
  const previousAffectRef = useRef(null);
  const lastSentContextRef = useRef(null);
  const processedTurnRef = useRef(null);
  const adaptationCandidateRef = useRef(null);
  const fusionRequestedTurnRef = useRef(null);
  const personaSentRef = useRef(false);
  const wsRef = useRef(null);
  const {
    status,
    isLive,
    isBusy,
    transcripts,
    lastEmotion,
    micMuted,
    callId,
    sessionToken,
    error,
    call,
    start,
    end,
    toggleMicMute,
    sendText,
  } = useVoiceCall(VOICE_OPTIONS);

  const emotion = liveEmotion || lastEmotion;
  const debug = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).has("avatarDebug");
  const latestTranscript = [...transcripts]
    .reverse()
    .find((transcript) => transcript.text);
  const latestFinalUserTranscript = [...transcripts]
    .reverse()
    .find((transcript) => (
      transcript.text
      && transcript.isFinal
      && String(transcript.speaker).toLowerCase() === "user"
    ));
  const latestTurnId = latestFinalUserTranscript
    ? String(
      latestFinalUserTranscript.ordinal
      ?? `${latestFinalUserTranscript.text.length}:${latestFinalUserTranscript.text.slice(0, 24)}`,
    )
    : null;

  const addTimeline = useCallback((item) => {
    const event = typeof item === "string" ? { message: item } : item;
    setTimeline((items) => [
      {
        at: Date.now(),
        type: event.type || "loop",
        message: event.message,
      },
      ...items,
    ].slice(0, 18));
  }, []);

  const {
    cameraStatus,
    faceObservation,
    faceScores,
    faceError,
    sampleCount,
    stopCamera,
    videoRef,
    cropRef,
  } = useFaceEmotionSampler({
    enabled: mode !== "voice",
    isLive,
    status,
    onTimeline: addTimeline,
  });

  const voiceObservation = useMemo(() => {
    if (scores) {
      return observationFromScores("voice", scores, {
        label: emotion,
        observedAt: Date.now(),
      });
    }
    if (emotion) {
      return observationFromScores("voice", {
        [emotion]: 0.65,
        neutral: 0.35,
      }, {
        label: emotion,
        confidence: 0.55,
        observedAt: Date.now(),
        detail: "label-only",
      });
    }
    return null;
  }, [emotion, scores]);

  const diagnosticAffectState = useMemo(() => {
    if (mode === "fusion") return null;
    const next = fuseAffect({
      face: mode === "voice" ? null : faceObservation,
      voice: mode === "face" ? null : voiceObservation,
      previous: previousAffectRef.current,
    });
    previousAffectRef.current = next;
    return next;
  }, [faceObservation, mode, voiceObservation]);
  const affectState = mode === "fusion"
    ? learnedAffectState
    : diagnosticAffectState;

  const displayEmotion = affectState?.label && affectState.label !== "uncertain"
    ? affectState.label
    : emotion;

  useEffect(() => {
    if (!isLive || !callId || !sessionToken || !VOICE_API_URL) return undefined;
    const url = `${toWsBase(VOICE_API_URL)}/v1/voice/emotion/stream`
      + `?callId=${encodeURIComponent(callId)}`
      + `&token=${encodeURIComponent(sessionToken)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type !== "emotion") return;
        if (message.scores) setScores(message.scores);
        if (message.emotion) setLiveEmotion(message.emotion);
        addTimeline(`voice ${message.emotion || "scores"} signal received`);
      } catch {
        // Ignore malformed diagnostics without interrupting the call.
      }
    };
    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [addTimeline, callId, isLive, sessionToken]);

  useEffect(() => {
    if (isLive) return;
    setScores(null);
    setLiveEmotion(null);
    setTimeline([]);
    setLastContext("");
    previousAffectRef.current = null;
    lastSentContextRef.current = null;
    processedTurnRef.current = null;
    adaptationCandidateRef.current = null;
    fusionRequestedTurnRef.current = null;
    personaSentRef.current = false;
    setLearnedAffectState(null);
    setAffectHistory([]);
  }, [isLive]);

  // Generates the post-session report once a call ends, from the affect
  // snapshots recorded per turn below. Runs on the same isLive-false render
  // that triggers the reset above -- this closure still holds the
  // pre-reset affectHistory/timeline, since the reset's setState calls
  // above only take effect on the following render.
  useEffect(() => {
    if (isLive || affectHistory.length === 0) return;
    setSessionReport(buildSessionReport({
      affectHistory,
      timeline,
      startedAt: callStartedAtRef.current,
      endedAt: Date.now(),
    }));
  }, [affectHistory, isLive, timeline]);

  // Visitors arriving from the "Build a Support Agent" wizard bring a
  // custom persona in router state. Prime the agent with it once, right
  // after the call connects, reusing the same sendText channel already
  // used for live affect-context adaptation above -- no server/Ultravox
  // call-creation changes needed.
  useEffect(() => {
    if (!isLive || !personaPrompt || personaSentRef.current) return;
    personaSentRef.current = true;
    try {
      sendText(personaPrompt, true);
      addTimeline(`persona primed: ${businessName || "custom business"}`);
    } catch (sendError) {
      addTimeline(`persona send failed: ${sendError.message}`);
    }
  }, [addTimeline, businessName, isLive, personaPrompt, sendText]);

  useEffect(() => {
    if (!isLive || mode !== "voice") return;
    stopCamera();
  }, [isLive, mode, stopCamera]);

  useEffect(() => {
    if (
      !isLive
      || mode !== "fusion"
      || !latestTurnId
      || !faceObservation
      || !voiceObservation
      || !callId
      || !sessionToken
      || !VOICE_API_URL
    ) return undefined;
    if (fusionRequestedTurnRef.current === latestTurnId) return undefined;
    fusionRequestedTurnRef.current = latestTurnId;

    const observedAt = Date.now();
    const url = `${VOICE_API_URL.replace(/\/+$/, "")}`
      + `/v1/voice/calls/${encodeURIComponent(callId)}/emotion/fusion/turn`;
    setLearnedAffectState(null);
    addTimeline(`fusion ${latestTurnId}: learned MLP requested`);
    void fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        turn_id: `${callId}:${latestTurnId}`,
        face: {
          scores: faceObservation.scores,
          label: faceObservation.label,
          confidence: faceObservation.confidence,
          quality: faceObservation.quality,
          observed_at: new Date(faceObservation.observedAt || observedAt).toISOString(),
          model_id: "exp15_best",
        },
        voice: {
          scores: voiceObservation.scores,
          label: voiceObservation.label,
          confidence: voiceObservation.confidence,
          quality: voiceObservation.quality,
          observed_at: new Date(voiceObservation.observedAt || observedAt).toISOString(),
          model_id: "wav2vec2-emotion",
        },
        max_voice_age_ms: 30000,
      }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.message || payload.error || `fusion HTTP ${response.status}`);
        }
        const next = affectStateFromApiFusion(payload, {
          face: faceObservation,
          voice: voiceObservation,
          observedAt,
          turnId: latestTurnId,
        });
        setLearnedAffectState(next);
        addTimeline(`fusion ${latestTurnId}: ${payload.fusion?.strategy || "API"} → ${next.label}`);
      })
      .catch((fusionError) => {
        setLearnedAffectState((current) => (
          current?.turnId === latestTurnId ? null : current
        ));
        addTimeline(`fusion ${latestTurnId} failed: ${fusionError.message}`);
      });

    return undefined;
  }, [
    addTimeline,
    callId,
    faceObservation,
    isLive,
    latestTurnId,
    mode,
    sessionToken,
    voiceObservation,
  ]);

  useEffect(() => {
    if (!isLive || !latestFinalUserTranscript || !affectState) return;
    const turnId = latestTurnId;
    if (mode === "fusion" && affectState.turnId !== turnId) return;
    if (processedTurnRef.current === turnId) return;
    processedTurnRef.current = turnId;

    setAffectHistory((history) => [...history, {
      at: Date.now(),
      turnId,
      label: affectState.label,
      valence: affectState.valence,
    }]);

    if (adaptationCandidateRef.current?.label !== affectState.label) {
      adaptationCandidateRef.current = null;
    }
    if (!shouldSendAffectContext(affectState, lastSentContextRef.current)) {
      adaptationCandidateRef.current = null;
      return;
    }

    const confirmation = confirmAdaptation(
      affectState,
      adaptationCandidateRef.current,
    );
    adaptationCandidateRef.current = confirmation.nextCandidate;
    if (!confirmation.allowed) {
      addTimeline(`adaptation held: confirm ${affectState.label} on another turn`);
      return;
    }

    const message = affectContextMessage(affectState);
    try {
      sendText(message, true);
      lastSentContextRef.current = sentContextFingerprint(affectState);
      setLastContext(message);
      addTimeline(`adaptation sent: ${affectState.policy.title}`);
    } catch (sendError) {
      addTimeline(`adaptation send failed: ${sendError.message}`);
    }
  }, [
    addTimeline,
    affectState,
    isLive,
    latestFinalUserTranscript,
    latestTurnId,
    mode,
    sendText,
  ]);

  function handleStart() {
    setAudioContext(ensureAudioContext());
    setSessionReport(null);
    callStartedAtRef.current = Date.now();
    addTimeline({
      type: "loop",
      message: mode === "voice"
        ? "starting voice-only detection"
        : "starting multimodal detection",
    });
    void start();
  }

  return (
    <>
      <Helmet>
        <title>Animated Emotion-Aware Voice Agent Demo — AIHumanity</title>
        <meta
          name="description"
          content="Talk with an animated realtime AI voice agent that observes vocal affect and adapts its response."
        />
      </Helmet>

      <div className="voice-agent-page">
        <TopMenuBar />

        <main className="voice-agent-main">
          <section className="voice-agent-intro">
            <span className="voice-agent-kicker">
              {businessName ? `Live Demo · Custom Agent for ${businessName}` : "Live Demo · Real-Time Emotion Read"}
            </span>
            <h1>{businessName ? `Talk to ${businessName}'s new support agent.` : "A voice agent you can see listening."}</h1>
            <p>
              {businessName
                ? `Speak naturally, as if you were a customer contacting ${businessName}. The agent reads your tone as you talk and adapts what it says next — in real time.`
                : "Speak naturally. The agent reads your tone and expression as you talk, then adapts what it says next — in real time."}
            </p>
            <div className="voice-agent-privacy">
              <strong>Private loop</strong>
              <span>Camera frames are cropped in browser; raw frames are not stored.</span>
            </div>
          </section>

          <section className="voice-agent-starter" aria-label="How to try this demo">
            <div>
              <span>1</span>
              <p>Allow mic access (camera too, if using Face + voice)</p>
            </div>
            <div>
              <span>2</span>
              <p>
                {businessName
                  ? `Ask a real question you'd ask ${businessName} — that works best`
                  : "Say something like \"My payment failed and I'm worried\" or \"I can't find a setting\""}
              </p>
            </div>
            <div>
              <span>3</span>
              <p>Watch the fused affect state and the agent's reply adapt</p>
            </div>
          </section>

          <section className="voice-agent-modebar" aria-label="Detection mode">
            {DETECTION_MODES.map((option) => (
              <button
                type="button"
                key={option.id}
                className={mode === option.id ? "active" : ""}
                onClick={() => setMode(option.id)}
                disabled={isBusy}
              >
                <span>{option.label}</span>
                <small>{option.sub}</small>
              </button>
            ))}
          </section>

          <section className="voice-agent-console">
            <div className="voice-agent-stage">
              <AnimatedVoiceFigure
                call={call}
                audioContext={audioContext}
                status={status}
                emotion={displayEmotion}
                characterId={VOICE_AVATAR_ID}
                debug={debug}
                className="voice-agent-figure"
              />
              <video
                ref={videoRef}
                className="voice-agent-face-video"
                muted
                playsInline
                aria-hidden="true"
              />
              <canvas
                ref={cropRef}
                className="voice-agent-face-crop"
                width="224"
                height="224"
                aria-hidden="true"
              />

              <div className="voice-agent-state">
                <span className={`voice-agent-state-dot state-${status}`} />
                <div>
                  <small>Realtime state</small>
                  <strong>{STATUS_COPY[status] || status}</strong>
                </div>
              </div>
            </div>

            <aside className="voice-agent-side">
              <div className="voice-agent-signal-head">
                <span>Fused affect state</span>
                <strong>{affectState?.label || "Waiting"}</strong>
                <small>
                  {affectState
                    ? `${Math.round(affectState.confidence * 100)}% confidence · ${affectState.policy.title}`
                    : "Detection → update → adaptation"}
                </small>
              </div>

              <div className="voice-agent-transcript">
                <span>Live transcript</span>
                <p>
                  {latestTranscript
                    ? `${latestTranscript.speaker === "agent" ? "Agent" : "You"}: ${latestTranscript.text}`
                    : "Start the call, then say hello."}
                </p>
              </div>

              <details className="voice-agent-internals">
                <summary>See the internals</summary>

                <div className="voice-agent-signal-grid">
                  <SignalCard
                    title="FER · exp15"
                    label={faceObservation?.label}
                    confidence={faceObservation?.confidence}
                    detail={faceError || `${cameraStatus}${sampleCount ? ` · ${sampleCount} samples` : ""}`}
                    active={mode !== "voice"}
                  />
                  <SignalCard
                    title="Voice · wav2vec2"
                    label={voiceObservation?.label}
                    confidence={voiceObservation?.confidence}
                    detail={scores ? "live audio scores" : "waiting for speech"}
                    active={mode !== "face"}
                  />
                </div>

                <div className="voice-agent-score-columns">
                  <div>
                    <span>Fusion scores</span>
                    <ScoreList
                      scores={affectState?.scores}
                      empty={mode === "fusion"
                        ? "Waiting for synchronized learned fusion."
                        : "Waiting for FER or voice evidence."}
                    />
                  </div>
                  <div>
                    <span>{mode === "face" ? "FER scores" : "Voice scores"}</span>
                    <ScoreList
                      scores={mode === "face" ? faceScores : scores}
                      empty={mode === "face" ? "Camera samples appear while listening." : "Voice scores appear after you speak."}
                    />
                  </div>
                </div>

                <div className="voice-agent-loop">
                  <span>Detect → update → adapt</span>
                  <Timeline items={timeline} />
                </div>

                {lastContext && (
                  <details className="voice-agent-context">
                    <summary>Last private adaptation</summary>
                    <pre>{lastContext}</pre>
                  </details>
                )}
              </details>
            </aside>
          </section>

          <section className="voice-agent-controls">
            {error && <p className="voice-agent-error">{error.message}</p>}
            {!isLive && !isBusy && (
              <button type="button" className="voice-agent-primary" onClick={handleStart}>
                Start live conversation
              </button>
            )}
            {isBusy && (
              <button type="button" className="voice-agent-primary" disabled>
                {status === CallStatus.CONNECTING ? "Connecting…" : "Ending…"}
              </button>
            )}
            {isLive && (
              <div className="voice-agent-live-controls">
                <button type="button" onClick={toggleMicMute}>
                  {micMuted ? "Unmute" : "Mute"}
                </button>
                <button type="button" className="danger" onClick={() => void end()}>
                  End conversation
                </button>
              </div>
            )}
            <p>Allow microphone access when prompted. You can interrupt the agent naturally.</p>
          </section>

          <SessionReportCard report={sessionReport} businessName={businessName} />

          <section className="voice-agent-cta">
            <div>
              <strong>Want this in your product?</strong>
              <span>The same detect → update → adapt loop is available as an API and SDK.</span>
            </div>
            <div className="voice-agent-cta-actions">
              <button type="button" className="voice-agent-cta-button primary" onClick={() => setApiModalOpen(true)}>
                Request API access
              </button>
              <Link
                className="voice-agent-cta-button secondary"
                to="/use-cases/support-de-escalation"
              >
                See the Support De-escalation use case
              </Link>
            </div>
          </section>
        </main>
      </div>

      <LeadCaptureModal
        open={apiModalOpen}
        onClose={() => setApiModalOpen(false)}
        kind="contact"
        context="Interested in: Live Voice Agent (Relationship Engine API)"
      />
    </>
  );
}
